import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import {
  GridColDef,
  GridColumnGroup,
  GridColumnGroupingModel,
} from '@mui/x-data-grid-premium';
import {
  GridColumnNode,
  GridLeafColumn,
} from '@mui/x-data-grid/models/gridColumnGrouping';
import classNames from 'classnames';
import { useMemo } from 'react';

import { theme } from '~/theme';

type PropsType = {
  columns: GridColDef[];
  columnGroupingModel?: GridColumnGroupingModel;
  sx?: SxProps<Theme>;
};

type FieldGroupClassNamePair = { field: string; groupClassName?: string };

type ColumnClassNameFn<P> = (params: P) => string;
type ColumnClassName<P> = string | ColumnClassNameFn<P>;

const extendColumnClassName = <P>(
  columnClassName?: ColumnClassName<P>,
  appendClassNames?: string,
) => {
  if (typeof columnClassName === 'string') {
    return classNames(columnClassName, appendClassNames);
  } else if (typeof columnClassName === 'function') {
    return (params: P) => {
      const fnClassNames = columnClassName(params);

      return classNames(fnClassNames, appendClassNames);
    };
  }

  return appendClassNames;
};

type UseDataGridStylingOutput = PropsType;
export const useDataGridGroupStyling = (
  props: PropsType,
): UseDataGridStylingOutput => {
  const { columns, columnGroupingModel, sx } = props;

  // Append column group classnames to Group Header Cells
  const { newColumnGroupingModel, groupClassNames } = useMemo(() => {
    let groupClassNames: string[] = [];

    const getGroupLevel = (
      columnGroups: GridColumnNode[],
      parentIndex: string,
      parentHeaderClassName: string,
    ) => {
      return columnGroups.map((colProps, index): GridColumnNode => {
        const groupIndex = !!parentIndex
          ? `${parentIndex}-${index + 1}`
          : `${index + 1}`;

        if (colProps.hasOwnProperty('groupId')) {
          const groupProps = colProps as GridColumnGroup;

          groupClassNames.push(`groupColumn-${groupIndex}`);

          const groupHeaderClassName = `${parentHeaderClassName} groupColumn-${groupIndex}`;
          const headerClassName = extendColumnClassName(
            groupProps.headerClassName,
            groupHeaderClassName,
          );

          return {
            ...groupProps,
            headerClassName,
            children: getGroupLevel(
              groupProps.children,
              groupIndex,
              groupHeaderClassName,
            ),
          };
        } else {
          return colProps;
        }
      });
    };

    return {
      newColumnGroupingModel: (!!columnGroupingModel
        ? getGroupLevel(columnGroupingModel, '', 'groupColumn')
        : []) as GridColumnGroup[],
      groupClassNames,
    };
  }, [columnGroupingModel]);

  // Append column group classnames to Column Header Cells & Content Cells
  const newColumns = useMemo(() => {
    // Pass if nothing to process
    if (!newColumnGroupingModel || newColumnGroupingModel?.length === 0) {
      return columns;
    }

    const getGroupClassNamesByColumn = (
      groupingModelLevel: GridColumnNode[],
      groupClassName?: string,
    ): FieldGroupClassNamePair[] => {
      return groupingModelLevel.reduce((acc, node) => {
        if (node.hasOwnProperty('field')) {
          const { field } = node as GridLeafColumn;
          acc.push({ field, groupClassName });
        } else if (node.hasOwnProperty('children')) {
          const { children, headerClassName } = node as GridColumnGroup;

          if (typeof headerClassName !== 'function') {
            return acc.concat(
              getGroupClassNamesByColumn(children, headerClassName),
            );
          }
        }

        return acc;
      }, [] as FieldGroupClassNamePair[]);
    };

    const groupClassNamesByColumn = getGroupClassNamesByColumn(
      newColumnGroupingModel,
    );

    return columns.map((column) => {
      const groupClass = groupClassNamesByColumn.find(
        (c) => c.field === column.field,
      )?.groupClassName;

      if (!!groupClass) {
        return {
          ...column,
          headerClassName: extendColumnClassName(
            column.headerClassName,
            groupClass,
          ),
          cellClassName: extendColumnClassName(
            column.cellClassName,
            groupClass,
          ),
        };
      }

      return column;
    });
  }, [newColumnGroupingModel, columns]);

  // Append sx with group column styles
  const newSx = useMemo(() => {
    const hasGroups = groupClassNames.length > 0;
    // Group styling is needed
    const getFirstAfterGroupChangeSelector = (
      groupClassNames: string[],
    ): string => {
      const cellClassNames = ['MuiDataGrid-cell', 'MuiDataGrid-columnHeader'];

      /** Generate css selector
       *  - First group column after non-group column (Start of column groups)
       *  - First column after last group column of each group (Between groups & end of column groups)
       */
      return `& ${[
        ...cellClassNames.map(
          (cellSelector) =>
            `.${cellSelector}:not(.groupColumn) + .${cellSelector}.groupColumn`,
        ),
        ...groupClassNames.map((groupSelector) => [
          ...cellClassNames.map(
            (cellSelector) =>
              `.${cellSelector}.${groupSelector} + .${cellSelector}:not(.${groupSelector})`,
          ),
        ]),
      ].join(', ')}`;
    };

    const groupStyling = hasGroups
      ? {
          [getFirstAfterGroupChangeSelector(groupClassNames)]: {
            borderLeft: `solid 1px rgba(0,0,0,0.12)`,
          },
        }
      : {};

    // Styling edge-case not handled by library
    const styleFixes = {
      '& .MuiDataGrid-row--detailPanelExpanded.MuiDataGrid-row--lastVisible': {
        '& .MuiDataGrid-cell': {
          borderBottomColor: theme.palette.grey['300'],
        },
      },
    };

    return { ...styleFixes, ...groupStyling, ...sx };
  }, [groupClassNames, sx]);

  return {
    columns: newColumns,
    columnGroupingModel: newColumnGroupingModel,
    sx: newSx,
  };
};
